import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ValidateSatisfactionReservationAPI } from "api/satisfaction";

import {
  CreateSatisfactionFormAPI,
  GetSatisfactionCountersAPI,
  GetSatisfactionFormAPI,
  GetSatisfactionFormByIdAPI,
  GetSatisfactionMultipleChoiceStatsAPI,
  GetSatisfactionRatingStatsAPI,
  GetSatisfactionTextSummaryAPI,
  GetRecentSatisfactionTextAnswersAPI,
  SubmitSatisfactionFormAPI,
  UpdateSatisfactionFormAPI,
} from "api/satisfaction";

const getErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message || error?.message || fallbackMessage;

// ================= CREATE FORM =================

export const createSatisfactionForm = createAsyncThunk(
  "satisfaction/createSatisfactionForm",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await CreateSatisfactionFormAPI(payload);

      return response?.data?.data?.form;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to create satisfaction form"));
    }
  }
);

// ================= GET FORM BY HOTEL OR RESERVATION =================

export const getSatisfactionForm = createAsyncThunk(
  "satisfaction/getSatisfactionForm",
  async (params, { rejectWithValue }) => {
    try {
      const response = await GetSatisfactionFormAPI(params);

      return response?.data?.data?.form;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch satisfaction form"));
    }
  }
);

// ================= GET FORM BY ID =================

export const getSatisfactionFormById = createAsyncThunk(
  "satisfaction/getSatisfactionFormById",
  async (formId, { rejectWithValue }) => {
    try {
      const response = await GetSatisfactionFormByIdAPI(formId);

      return response?.data?.data?.form;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch satisfaction form details"));
    }
  }
);

// ================= UPDATE FORM =================

export const updateSatisfactionForm = createAsyncThunk(
  "satisfaction/updateSatisfactionForm",
  async ({ formId, payload }, { rejectWithValue }) => {
    try {
      const response = await UpdateSatisfactionFormAPI({
        formId,
        data: payload,
      });

      return response?.data?.data?.form;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to update satisfaction form"));
    }
  }
);

// ================= VALIDATE PUBLIC RESERVATION =================

export const validateSatisfactionReservation = createAsyncThunk(
  "satisfaction/validateSatisfactionReservation",
  async (reservationId, { rejectWithValue }) => {
    try {
      if (!reservationId) {
        return rejectWithValue("Reservation ID is required");
      }

      const response = await ValidateSatisfactionReservationAPI(reservationId);

      return response?.data?.data || null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to validate reservation"));
    }
  }
);

// ================= SUBMIT FORM =================

export const submitSatisfactionForm = createAsyncThunk(
  "satisfaction/submitSatisfactionForm",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await SubmitSatisfactionFormAPI(payload);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to submit satisfaction form"));
    }
  }
);

// ================= COUNTER STATISTICS =================

export const getSatisfactionCounters = createAsyncThunk(
  "satisfaction/getSatisfactionCounters",
  async (params, { rejectWithValue }) => {
    try {
      const response = await GetSatisfactionCountersAPI(params);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch satisfaction counters"));
    }
  }
);

// ================= TEXT SUMMARY =================

export const getSatisfactionTextSummary = createAsyncThunk(
  "satisfaction/getSatisfactionTextSummary",
  async (params, { rejectWithValue }) => {
    try {
      const response = await GetSatisfactionTextSummaryAPI(params);

      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch satisfaction text summary"));
    }
  }
);

// ================= RATING STATISTICS =================

export const getSatisfactionRatingStats = createAsyncThunk(
  "satisfaction/getSatisfactionRatingStats",
  async (params, { rejectWithValue }) => {
    try {
      const response = await GetSatisfactionRatingStatsAPI(params);

      return response?.data?.data || [];
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch satisfaction rating statistics")
      );
    }
  }
);

// ================= MULTIPLE CHOICE STATISTICS =================

export const getSatisfactionMultipleChoiceStats = createAsyncThunk(
  "satisfaction/getSatisfactionMultipleChoiceStats",
  async (params, { rejectWithValue }) => {
    try {
      const response = await GetSatisfactionMultipleChoiceStatsAPI(params);

      return response?.data?.data || [];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch multiple choice statistics"));
    }
  }
);

// ================= RECENT TEXT ANSWERS =================

export const getRecentSatisfactionTextAnswers = createAsyncThunk(
  "satisfaction/getRecentSatisfactionTextAnswers",
  async (params, { rejectWithValue }) => {
    try {
      const response = await GetRecentSatisfactionTextAnswersAPI(params);

      return response?.data?.data || [];
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch recent satisfaction responses")
      );
    }
  }
);

const initialState = {
  satisfactionForm: null,
  satisfactionFormDetails: null,

  submissionResult: null,

  publicValidationResult: null,
  submissionResult: null,

  counters: null,
  textSummary: null,
  ratingStats: [],
  multipleChoiceStats: [],
  recentTextAnswers: [],

  // Create form
  createFormLoading: false,
  createFormSuccess: false,
  createFormError: null,

  // Get form by hotel/reservation
  getFormLoading: false,
  getFormSuccess: false,
  getFormError: null,

  // Get form by ID
  getFormDetailsLoading: false,
  getFormDetailsSuccess: false,
  getFormDetailsError: null,

  // Update form
  updateFormLoading: false,
  updateFormSuccess: false,
  updateFormError: null,

  // Public reservation validation
  validateReservationLoading: false,
  validateReservationSuccess: false,
  validateReservationError: null,

  // Public form submission
  submitFormLoading: false,
  submitFormSuccess: false,
  submitFormError: null,

  // Counters
  countersLoading: false,
  countersSuccess: false,
  countersError: null,

  // Text summary
  textSummaryLoading: false,
  textSummarySuccess: false,
  textSummaryError: null,

  // Rating stats
  ratingStatsLoading: false,
  ratingStatsSuccess: false,
  ratingStatsError: null,

  // Multiple choice stats
  multipleChoiceStatsLoading: false,
  multipleChoiceStatsSuccess: false,
  multipleChoiceStatsError: null,

  // Recent text answers
  recentTextAnswersLoading: false,
  recentTextAnswersSuccess: false,
  recentTextAnswersError: null,
};

const satisfactionSlice = createSlice({
  name: "satisfaction",
  initialState,

  reducers: {
    resetCreateSatisfactionFormState: (state) => {
      state.createFormLoading = false;
      state.createFormSuccess = false;
      state.createFormError = null;
    },

    resetGetSatisfactionFormState: (state) => {
      state.getFormLoading = false;
      state.getFormSuccess = false;
      state.getFormError = null;
    },

    resetSatisfactionFormDetailsState: (state) => {
      state.satisfactionFormDetails = null;
      state.getFormDetailsLoading = false;
      state.getFormDetailsSuccess = false;
      state.getFormDetailsError = null;
    },

    resetUpdateSatisfactionFormState: (state) => {
      state.updateFormLoading = false;
      state.updateFormSuccess = false;
      state.updateFormError = null;
    },

    resetSubmitSatisfactionFormState: (state) => {
      state.submissionResult = null;
      state.submitFormLoading = false;
      state.submitFormSuccess = false;
      state.submitFormError = null;
    },

    clearSatisfactionForm: (state) => {
      state.satisfactionForm = null;
      state.satisfactionFormDetails = null;
    },

    resetSatisfactionCountersState: (state) => {
      state.counters = null;
      state.countersLoading = false;
      state.countersSuccess = false;
      state.countersError = null;
    },

    resetSatisfactionTextSummaryState: (state) => {
      state.textSummary = null;
      state.textSummaryLoading = false;
      state.textSummarySuccess = false;
      state.textSummaryError = null;
    },

    resetSatisfactionRatingStatsState: (state) => {
      state.ratingStats = [];
      state.ratingStatsLoading = false;
      state.ratingStatsSuccess = false;
      state.ratingStatsError = null;
    },

    resetSatisfactionMultipleChoiceStatsState: (state) => {
      state.multipleChoiceStats = [];
      state.multipleChoiceStatsLoading = false;
      state.multipleChoiceStatsSuccess = false;
      state.multipleChoiceStatsError = null;
    },

    resetRecentSatisfactionTextAnswersState: (state) => {
      state.recentTextAnswers = [];
      state.recentTextAnswersLoading = false;
      state.recentTextAnswersSuccess = false;
      state.recentTextAnswersError = null;
    },

    resetValidateSatisfactionReservationState: (state) => {
      state.publicValidationResult = null;
      state.validateReservationLoading = false;
      state.validateReservationSuccess = false;
      state.validateReservationError = null;
    },

    resetSatisfactionStatisticsState: (state) => {
      state.counters = null;
      state.textSummary = null;
      state.ratingStats = [];
      state.multipleChoiceStats = [];
      state.recentTextAnswers = [];

      state.countersLoading = false;
      state.countersSuccess = false;
      state.countersError = null;

      state.textSummaryLoading = false;
      state.textSummarySuccess = false;
      state.textSummaryError = null;

      state.ratingStatsLoading = false;
      state.ratingStatsSuccess = false;
      state.ratingStatsError = null;

      state.multipleChoiceStatsLoading = false;
      state.multipleChoiceStatsSuccess = false;
      state.multipleChoiceStatsError = null;

      state.recentTextAnswersLoading = false;
      state.recentTextAnswersSuccess = false;
      state.recentTextAnswersError = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // ================= CREATE FORM =================
      .addCase(createSatisfactionForm.pending, (state) => {
        state.createFormLoading = true;
        state.createFormSuccess = false;
        state.createFormError = null;
      })
      .addCase(createSatisfactionForm.fulfilled, (state, action) => {
        state.createFormLoading = false;
        state.createFormSuccess = true;

        state.satisfactionForm = action.payload || null;
        state.satisfactionFormDetails = action.payload || null;
      })
      .addCase(createSatisfactionForm.rejected, (state, action) => {
        state.createFormLoading = false;
        state.createFormSuccess = false;
        state.createFormError = action.payload;
      })

      // ================= GET FORM =================
      .addCase(getSatisfactionForm.pending, (state) => {
        state.getFormLoading = true;
        state.getFormSuccess = false;
        state.getFormError = null;
      })
      .addCase(getSatisfactionForm.fulfilled, (state, action) => {
        state.getFormLoading = false;
        state.getFormSuccess = true;
        state.satisfactionForm = action.payload || null;
      })
      .addCase(getSatisfactionForm.rejected, (state, action) => {
        state.getFormLoading = false;
        state.getFormSuccess = false;
        state.getFormError = action.payload;
        state.satisfactionForm = null;
      })

      // ================= GET FORM BY ID =================
      .addCase(getSatisfactionFormById.pending, (state) => {
        state.getFormDetailsLoading = true;
        state.getFormDetailsSuccess = false;
        state.getFormDetailsError = null;
      })
      .addCase(getSatisfactionFormById.fulfilled, (state, action) => {
        state.getFormDetailsLoading = false;
        state.getFormDetailsSuccess = true;
        state.satisfactionFormDetails = action.payload || null;
      })
      .addCase(getSatisfactionFormById.rejected, (state, action) => {
        state.getFormDetailsLoading = false;
        state.getFormDetailsSuccess = false;
        state.getFormDetailsError = action.payload;
        state.satisfactionFormDetails = null;
      })

      // ================= UPDATE FORM =================
      .addCase(updateSatisfactionForm.pending, (state) => {
        state.updateFormLoading = true;
        state.updateFormSuccess = false;
        state.updateFormError = null;
      })
      .addCase(updateSatisfactionForm.fulfilled, (state, action) => {
        state.updateFormLoading = false;
        state.updateFormSuccess = true;

        state.satisfactionForm = action.payload || null;
        state.satisfactionFormDetails = action.payload || null;
      })
      .addCase(updateSatisfactionForm.rejected, (state, action) => {
        state.updateFormLoading = false;
        state.updateFormSuccess = false;
        state.updateFormError = action.payload;
      })

      // ================= VALIDATE PUBLIC RESERVATION =================
      .addCase(validateSatisfactionReservation.pending, (state) => {
        state.validateReservationLoading = true;
        state.validateReservationSuccess = false;
        state.validateReservationError = null;
        state.publicValidationResult = null;
      })
      .addCase(validateSatisfactionReservation.fulfilled, (state, action) => {
        state.validateReservationLoading = false;
        state.validateReservationSuccess = true;
        state.validateReservationError = null;
        state.publicValidationResult = action.payload || null;
      })
      .addCase(validateSatisfactionReservation.rejected, (state, action) => {
        state.validateReservationLoading = false;
        state.validateReservationSuccess = false;
        state.validateReservationError = action.payload || "Failed to validate reservation";
        state.publicValidationResult = null;
      })

      // ================= SUBMIT FORM =================
      .addCase(submitSatisfactionForm.pending, (state) => {
        state.submitFormLoading = true;
        state.submitFormSuccess = false;
        state.submitFormError = null;
        state.submissionResult = null;
      })
      .addCase(submitSatisfactionForm.fulfilled, (state, action) => {
        state.submitFormLoading = false;
        state.submitFormSuccess = true;
        state.submissionResult = action.payload || null;
      })
      .addCase(submitSatisfactionForm.rejected, (state, action) => {
        state.submitFormLoading = false;
        state.submitFormSuccess = false;
        state.submitFormError = action.payload;
      })

      // ================= COUNTERS =================
      .addCase(getSatisfactionCounters.pending, (state) => {
        state.countersLoading = true;
        state.countersSuccess = false;
        state.countersError = null;
      })
      .addCase(getSatisfactionCounters.fulfilled, (state, action) => {
        state.countersLoading = false;
        state.countersSuccess = true;
        state.counters = action.payload || null;
      })
      .addCase(getSatisfactionCounters.rejected, (state, action) => {
        state.countersLoading = false;
        state.countersSuccess = false;
        state.countersError = action.payload;
      })

      // ================= TEXT SUMMARY =================
      .addCase(getSatisfactionTextSummary.pending, (state) => {
        state.textSummaryLoading = true;
        state.textSummarySuccess = false;
        state.textSummaryError = null;
      })
      .addCase(getSatisfactionTextSummary.fulfilled, (state, action) => {
        state.textSummaryLoading = false;
        state.textSummarySuccess = true;
        state.textSummary = action.payload || null;
      })
      .addCase(getSatisfactionTextSummary.rejected, (state, action) => {
        state.textSummaryLoading = false;
        state.textSummarySuccess = false;
        state.textSummaryError = action.payload;
      })

      // ================= RATING STATS =================
      .addCase(getSatisfactionRatingStats.pending, (state) => {
        state.ratingStatsLoading = true;
        state.ratingStatsSuccess = false;
        state.ratingStatsError = null;
      })
      .addCase(getSatisfactionRatingStats.fulfilled, (state, action) => {
        state.ratingStatsLoading = false;
        state.ratingStatsSuccess = true;
        state.ratingStats = action.payload || [];
      })
      .addCase(getSatisfactionRatingStats.rejected, (state, action) => {
        state.ratingStatsLoading = false;
        state.ratingStatsSuccess = false;
        state.ratingStatsError = action.payload;
      })

      // ================= MULTIPLE CHOICE STATS =================
      .addCase(getSatisfactionMultipleChoiceStats.pending, (state) => {
        state.multipleChoiceStatsLoading = true;
        state.multipleChoiceStatsSuccess = false;
        state.multipleChoiceStatsError = null;
      })
      .addCase(getSatisfactionMultipleChoiceStats.fulfilled, (state, action) => {
        state.multipleChoiceStatsLoading = false;
        state.multipleChoiceStatsSuccess = true;
        state.multipleChoiceStats = action.payload || [];
      })
      .addCase(getSatisfactionMultipleChoiceStats.rejected, (state, action) => {
        state.multipleChoiceStatsLoading = false;
        state.multipleChoiceStatsSuccess = false;
        state.multipleChoiceStatsError = action.payload;
      })

      // ================= RECENT TEXT ANSWERS =================
      .addCase(getRecentSatisfactionTextAnswers.pending, (state) => {
        state.recentTextAnswersLoading = true;
        state.recentTextAnswersSuccess = false;
        state.recentTextAnswersError = null;
      })
      .addCase(getRecentSatisfactionTextAnswers.fulfilled, (state, action) => {
        state.recentTextAnswersLoading = false;
        state.recentTextAnswersSuccess = true;
        state.recentTextAnswers = action.payload || [];
      })
      .addCase(getRecentSatisfactionTextAnswers.rejected, (state, action) => {
        state.recentTextAnswersLoading = false;
        state.recentTextAnswersSuccess = false;
        state.recentTextAnswersError = action.payload;
      });
  },
});

export const {
  resetCreateSatisfactionFormState,
  resetGetSatisfactionFormState,
  resetSatisfactionFormDetailsState,
  resetUpdateSatisfactionFormState,
  resetValidateSatisfactionReservationState,
  resetSubmitSatisfactionFormState,
  clearSatisfactionForm,
  resetSatisfactionCountersState,
  resetSatisfactionTextSummaryState,
  resetSatisfactionRatingStatsState,
  resetSatisfactionMultipleChoiceStatsState,
  resetRecentSatisfactionTextAnswersState,
  resetSatisfactionStatisticsState,
} = satisfactionSlice.actions;

export default satisfactionSlice.reducer;
